'use client'

import { useEffect, useState } from 'react'

type ScriptOptions = {
  id?: string
  dataSessionId?: string
  async?: boolean
  defer?: boolean
  type?: string
  crossOrigin?: string
  nonce?: string
  referrerPolicy?: string
  integrity?: string
}

export const useImportScript = (
  url: string,
  options: ScriptOptions = { async: true },
  callback?: () => void,
) => {
  const [isLoaded, setIsLoaded] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!url) {
      return
    }

    // For some reason there seems to be cases where scripts get added more than once
    // so this resolves that
    if (options.id) {
      const existingScript = document.getElementById(options.id)
      if (existingScript) {
        if (existingScript.getAttribute('src') === url) {
          setIsLoaded(true)
          if (callback) {
            callback()
          }
          return
        }
        existingScript.parentNode?.removeChild(existingScript)
      }
    }

    const script = document.createElement('script')

    if (options.id) {
      script.id = options.id
    }
    script.src = url
    // Use setAttribute for data attributes
    if (options.dataSessionId) {
      script.setAttribute('data-session-id', options.dataSessionId)
    }
    script.async = options.async ?? true
    script.defer = options.defer ?? false
    script.type = options.type || 'text/javascript'
    if (options.crossOrigin) {
      script.crossOrigin = options.crossOrigin
    }
    if (options.nonce) {
      script.nonce = options.nonce
    }
    if (options.referrerPolicy) {
      script.referrerPolicy = options.referrerPolicy
    }
    if (options.integrity) {
      script.integrity = options.integrity
    }

    const onLoad = () => {
      setIsLoaded(true)

      if (callback) {
        callback()
      }
    }

    const onError = (error: Event | null) => {
      if (error) {
        setError(new Error('Error loading script'))
      }
    }

    script.addEventListener('load', onLoad)
    script.addEventListener('error', onError)

    document.body.appendChild(script)

    return () => {
      script.removeEventListener('load', onLoad)
      script.removeEventListener('error', onError)
    }
  }, [url, options.id, options.async, options.dataSessionId])

  return [isLoaded, error]
}
